import {
  ESCALATION_LEVEL_BANDS,
  escalationLevel,
  escalationPreviewFor,
  type EscalationCauseDef,
  type EscalationLevel,
  type MissionId,
} from '@badger-sentinel/rules';

/**
 * The escalation meter (RB §7.1, shell.md §Escalation).
 *
 * The counter and the level are both public to both teams, so nothing here is a
 * fog-of-war question — it is a display question, and the display is chrome
 * because every decision in RB §7 is priced against it.
 */

/**
 * The meter's full scale.
 *
 * The counter itself is unbounded; this is where the *bar* runs out. 26 is the
 * design's scale and it is the value that puts the three band ticks exactly
 * where the design draws them — 6/26 = 23.1 %, 13/26 = 50 %, 21/26 = 80.8 %.
 * A counter past it pins the fill at full; the number beside the bar is always
 * the true one.
 */
export const ESCALATION_METER_MAX = 26;

export interface EscalationTick {
  readonly level: EscalationLevel;
  readonly atOrAbove: number;
  readonly pct: number;
}

/** The band boundaries, ascending, with the zero band dropped — it is the floor. */
export const ESCALATION_TICKS: readonly EscalationTick[] = Object.freeze(
  [...ESCALATION_LEVEL_BANDS]
    .filter((band) => band.atOrAbove > 0)
    .sort((a, b) => a.atOrAbove - b.atOrAbove)
    .map((band) => ({
      level: band.level,
      atOrAbove: band.atOrAbove,
      pct: (band.atOrAbove / ESCALATION_METER_MAX) * 100,
    })),
);

export function meterPct(counter: number): number {
  return Math.max(0, Math.min(100, (counter / ESCALATION_METER_MAX) * 100));
}

export function bandOf(counter: number): EscalationLevel {
  return escalationLevel(counter);
}

/**
 * What the seat's queued orders may add.
 *
 * **A range, never a figure.** Four things stop this being one number and all
 * four are real: a mission may draw several `ESCALATION_CAUSES` rows and only
 * the resolving phase knows which (`LAUNCH` draws three); the nuclear and
 * dual-use multipliers depend on target flags a seat may not have been given; an
 * `ON_ATTRIBUTION` row is banked unapplied and may never land at all (RB §7.4);
 * and a **`conditional`** row is not banked at all unless something beyond the
 * act itself happens.
 *
 * So the floor counts only rows that are both applied on the spot *and* banked
 * whatever happens, the ceiling counts every row a mission can draw, and neither
 * is multiplied — which makes the ceiling a floor of its own. The `≥` in the
 * label says so.
 *
 * `ENGAGE` is the case that made the fourth reason necessary. Its two rows are
 * `KKV_ATTEMPT` (certain to be banked, charged only on attribution) and
 * `KKV_KILL` (charged the moment it exists, banked only on a kill). Counting the
 * second as a floor claimed `≥ +8` for an engagement that may miss — which the
 * counter would then disagree with, in front of the crew.
 */
export interface EscalationProjection {
  readonly minAdd: number;
  readonly maxAdd: number;
  /** Where the counter could land — the two ends are equal when the band is certain. */
  readonly bandLow: EscalationLevel;
  readonly bandHigh: EscalationLevel;
  readonly label: string;
}

/**
 * The least this set of rows can add: zero unless some row lands unconditionally.
 *
 * One definition, read by the status row's whole-queue projection and by the
 * builder's per-order preview, so the two cannot say different things about the
 * same order.
 */
export function floorOf(causes: readonly EscalationCauseDef[]): number {
  const certain = causes.filter((cause) => cause.mode !== 'ON_ATTRIBUTION' && !cause.conditional);
  return certain.length === 0 ? 0 : Math.min(...certain.map((cause) => cause.value));
}

export function projectEscalation(
  counter: number,
  missions: readonly MissionId[],
): EscalationProjection | null {
  let minAdd = 0;
  let maxAdd = 0;

  for (const mission of missions) {
    const causes = escalationPreviewFor(mission);
    if (causes.length === 0) continue;
    minAdd += floorOf(causes);
    maxAdd += Math.max(...causes.map((cause) => cause.value));
  }

  if (maxAdd === 0) return null;

  return {
    minAdd,
    maxAdd,
    bandLow: escalationLevel(counter + minAdd),
    bandHigh: escalationLevel(counter + maxAdd),
    label: minAdd === maxAdd ? `≥ +${minAdd}` : `+${minAdd}–${maxAdd}`,
  };
}
